import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";
import "./AdminSystemPage.responsive.css";

function clampPercent(value) {
  const next = Number(value || 0);
  return Math.max(0, Math.min(100, next));
}

function safeNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function getInitials(value) {
  return String(value || "US").slice(0, 2).toUpperCase();
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function UsageBar({ value }) {
  return (
    <div className="mgadmin-usage-bar">
      <i style={{ width: `${clampPercent(value)}%` }} />
    </div>
  );
}

function StatusDot({ ok = true }) {
  return <span className={ok ? "mgadmin-status-dot ok" : "mgadmin-status-dot bad"} />;
}

function AdminStatCard({ label, value, helper, primary = false }) {
  return (
    <div className={primary ? "mgadmin-stat primary" : "mgadmin-stat"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function LiveWave({ cpu = 0, memory = 0, disk = 0 }) {
  const points = useMemo(() => {
    const cpuFactor = clampPercent(cpu) / 100;
    const memFactor = clampPercent(memory) / 100;
    const diskFactor = clampPercent(disk) / 100;

    const rows = [];
    const width = 720;
    const height = 180;
    const mid = height / 2;

    for (let i = 0; i <= 90; i += 1) {
      const x = (i / 90) * width;
      const spike =
        Math.sin(i * 0.55) * (18 + cpuFactor * 28) +
        Math.sin(i * 0.17) * (8 + memFactor * 18) +
        Math.cos(i * 0.31) * (5 + diskFactor * 10);

      const pulse = i % 18 === 0 ? -36 * (0.4 + cpuFactor) : 0;
      const recovery = i % 18 === 1 ? 24 * (0.3 + memFactor) : 0;

      const y = mid + spike + pulse + recovery;
      rows.push(`${x.toFixed(2)},${Math.max(16, Math.min(height - 16, y)).toFixed(2)}`);
    }

    return rows.join(" ");
  }, [cpu, memory, disk]);

  return (
    <div className="mgadmin-live-wave-card">
      <div className="mgadmin-live-wave-head">
        <div>
          <span>Live Server ECG</span>
          <strong>Runtime pulse simulation</strong>
        </div>
        <em>Streaming</em>
      </div>

      <div className="mgadmin-wave-wrap">
        <svg viewBox="0 0 720 180" preserveAspectRatio="none">
          <defs>
            <linearGradient id="mgWaveGradient" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#18b7ff" />
              <stop offset="50%" stopColor="#62d7ff" />
              <stop offset="100%" stopColor="#1f7bc1" />
            </linearGradient>
          </defs>

          <g className="mgadmin-wave-grid">
            {Array.from({ length: 9 }).map((_, index) => (
              <line
                key={`h-${index}`}
                x1="0"
                x2="720"
                y1={index * 22.5}
                y2={index * 22.5}
              />
            ))}
            {Array.from({ length: 13 }).map((_, index) => (
              <line
                key={`v-${index}`}
                y1="0"
                y2="180"
                x1={index * 60}
                x2={index * 60}
              />
            ))}
          </g>

          <polyline className="mgadmin-wave-line glow" points={points} />
          <polyline className="mgadmin-wave-line main" points={points} />
        </svg>

        <div className="mgadmin-wave-scan" />
      </div>

      <div className="mgadmin-wave-metrics">
        <div>
          <span>CPU</span>
          <strong>{Math.round(cpu)}%</strong>
        </div>
        <div>
          <span>RAM</span>
          <strong>{Math.round(memory)}%</strong>
        </div>
        <div>
          <span>DISK</span>
          <strong>{Math.round(disk)}%</strong>
        </div>
      </div>
    </div>
  );
}

function SystemSignal({ overview }) {
  const cpu = overview?.cpu?.load_percent_estimate || 0;
  const memory = overview?.memory?.used_percent || 0;
  const disk = overview?.disk_root?.used_percent || 0;

  const bars = [
    {
      label: "CPU",
      value: Math.round(cpu),
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "RAM",
      value: Math.round(memory),
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Disk",
      value: Math.round(disk),
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <div className="mgadmin-signal-card">
      <div className="mgadmin-signal-head">
        <div>
          <span>Server Signal</span>
          <strong>Machine behavior</strong>
        </div>
        <em>Live</em>
      </div>

      <div className="mgadmin-bars">
        {bars.map((bar) => (
          <div className="mgadmin-bar-item" key={bar.label}>
            <div className="mgadmin-bar-track">
              <i
                style={{
                  height: `${Math.max(18, bar.value)}%`,
                  background: bar.color,
                }}
              />
            </div>
            <b>{bar.value}%</b>
            <small>{bar.label}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function DockerLocalStatus({ overview }) {
  const dockerAvailable = Boolean(overview?.docker?.available);
  const containers = overview?.docker?.containers || [];
  const runningCount = containers.length;

  return (
    <div className="mgadmin-runtime-card">
      <div className="mgadmin-runtime-head">
        <div>
          <span>Runtime Mode</span>
          <strong>{dockerAvailable ? "Docker-aware host" : "Local / limited host"}</strong>
        </div>
        <StatusDot ok={dockerAvailable} />
      </div>

      <p>
        {dockerAvailable
          ? "Docker socket is reachable. Container status and logs can be inspected from this admin page."
          : "Docker socket is not reachable. System metrics still work, but container logs may be unavailable."}
      </p>

      <div className="mgadmin-runtime-grid">
        <div>
          <span>Docker</span>
          <strong>{dockerAvailable ? "Connected" : "Unavailable"}</strong>
        </div>
        <div>
          <span>Containers</span>
          <strong>{runningCount}</strong>
        </div>
        <div>
          <span>Machine</span>
          <strong>{overview?.host?.machine || "—"}</strong>
        </div>
      </div>

      {dockerAvailable && containers.length > 0 && (
        <div className="mgadmin-container-list">
          {containers.slice(0, 5).map((item, index) => (
            <div key={`${item.ID || item.Names || index}`} className="mgadmin-container-row">
              <StatusDot ok />
              <span>{item.Names || item.Image || "container"}</span>
              <em>{item.Status || "running"}</em>
            </div>
          ))}
        </div>
      )}

      {!dockerAvailable && overview?.docker?.error && (
        <pre className="mgadmin-runtime-error">{overview.docker.error}</pre>
      )}
    </div>
  );
}

function QuotaModal({ user, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    patient_limit: 2,
    sessions_per_patient: 2,
    valid_until: "",
    is_enabled: true,
    note: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;

    setForm({
      patient_limit: user.quota?.patient_limit ?? 2,
      sessions_per_patient: user.quota?.sessions_per_patient ?? 2,
      valid_until: user.quota?.valid_until
        ? String(user.quota.valid_until).slice(0, 10)
        : "",
      is_enabled: user.quota?.is_enabled ?? true,
      note: user.quota?.note || "",
    });
  }, [user]);

  if (!open || !user) return null;

  const patientLimit = safeNumber(form.patient_limit, 0);
  const sessionsPerPatient = safeNumber(form.sessions_per_patient, 0);
  const totalSessionLimit = patientLimit * sessionsPerPatient;

  const saveQuota = async () => {
    try {
      setSaving(true);

      await api.post(`/api/admin/usage/user/${user.user_id}/quota`, {
        patient_limit: Number(form.patient_limit || 0),
        sessions_per_patient: Number(form.sessions_per_patient || 0),
        valid_until: form.valid_until || null,
        is_enabled: Boolean(form.is_enabled),
        note: form.note || null,
      });

      await onSaved();
      onClose();
      alert("Quota updated successfully");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update quota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mgadmin-modal-backdrop" onMouseDown={onClose}>
      <div className="mgadmin-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mgadmin-modal-head">
          <div className="mgadmin-modal-user">
            <div className="mgadmin-avatar large">
              {getInitials(user.name || user.email)}
            </div>
            <div>
              <span>Edit User Quota</span>
              <strong>{user.name || "Unnamed User"}</strong>
              <p>{user.email || "No email"} · {user.role || "user"}</p>
            </div>
          </div>

          <button type="button" className="mgadmin-icon-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="mgadmin-modal-status-grid">
          <div>
            <span>Current patients</span>
            <strong>{user.patient_count}</strong>
          </div>
          <div>
            <span>Current sessions</span>
            <strong>{user.session_count}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{user.account_status || "active"}</strong>
          </div>
          <div>
            <span>Last update</span>
            <strong>{formatDate(user.quota?.updated_at)}</strong>
          </div>
        </div>

        <div className="mgadmin-editor-grid">
          <div className="mgadmin-field">
            <label>Patient limit</label>
            <input
              type="number"
              min="0"
              value={form.patient_limit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, patient_limit: e.target.value }))
              }
            />
          </div>

          <div className="mgadmin-field">
            <label>Sessions per patient</label>
            <input
              type="number"
              min="0"
              value={form.sessions_per_patient}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  sessions_per_patient: e.target.value,
                }))
              }
            />
          </div>

          <div className="mgadmin-field">
            <label>Valid until</label>
            <input
              type="date"
              value={form.valid_until}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, valid_until: e.target.value }))
              }
            />
          </div>

          <div className="mgadmin-field">
            <label>Quota enabled</label>
            <select
              value={form.is_enabled ? "yes" : "no"}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  is_enabled: e.target.value === "yes",
                }))
              }
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </div>
        </div>

        <div className="mgadmin-current-usage">
          <div>
            <span>Patient Usage</span>
            <strong>
              {user.patient_count}/{patientLimit}
            </strong>
            <UsageBar value={(user.patient_count / Math.max(patientLimit, 1)) * 100} />
          </div>

          <div>
            <span>Session Usage</span>
            <strong>
              {user.session_count}/{totalSessionLimit}
            </strong>
            <UsageBar
              value={(user.session_count / Math.max(totalSessionLimit, 1)) * 100}
            />
          </div>
        </div>

        <div className="mgadmin-field">
          <label>Admin note</label>
          <textarea
            rows={4}
            value={form.note}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, note: e.target.value }))
            }
            placeholder="Reason, payment note, trial extension, internal remark..."
          />
        </div>

        <div className="mgadmin-modal-actions">
          <button type="button" className="mgadmin-btn soft" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="mgadmin-btn primary"
            onClick={saveQuota}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Quota"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersTable({ users, onSelectUser }) {
  return (
    <div className="mgadmin-table-wrap">
      <table className="mgadmin-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Status</th>
            <th>Patients</th>
            <th>Sessions</th>
            <th>Valid Until</th>
            <th>Quota</th>
          </tr>
        </thead>

        <tbody>
          {users.map((item) => {
            const patientPercent = item.usage?.patient_percent || 0;
            const sessionPercent = item.usage?.session_percent || 0;
            const danger =
              item.usage?.patient_limit_reached || item.usage?.session_limit_reached;

            return (
              <tr key={item.user_id} onClick={() => onSelectUser(item)}>
                <td>
                  <div className="mgadmin-user-cell">
                    <div className="mgadmin-avatar">
                      {getInitials(item.name || item.email)}
                    </div>
                    <div>
                      <strong>{item.name || "Unnamed User"}</strong>
                      <p>{item.email || "No email"}</p>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="mgadmin-pill">{item.role || "user"}</span>
                </td>

                <td>
                  <span className={danger ? "mgadmin-pill danger" : "mgadmin-pill ok"}>
                    {danger ? "limit reached" : item.account_status || "active"}
                  </span>
                </td>

                <td>
                  <div className="mgadmin-table-metric">
                    <strong>
                      {item.patient_count}/{item.quota?.patient_limit}
                    </strong>
                    <UsageBar value={patientPercent} />
                  </div>
                </td>

                <td>
                  <div className="mgadmin-table-metric">
                    <strong>
                      {item.session_count}/{item.quota?.total_session_limit}
                    </strong>
                    <UsageBar value={sessionPercent} />
                  </div>
                </td>

                <td>{item.quota?.valid_until || "Trial/default"}</td>

                <td>
                  <button
                    type="button"
                    className="mgadmin-mini-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectUser(item);
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminSystemPage() {
  const [overview, setOverview] = useState(null);
  const [usagePayload, setUsagePayload] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);

  const [logs, setLogs] = useState("");
  const [logService, setLogService] = useState("backend");
  const [logTail, setLogTail] = useState(200);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const refreshTimerRef = useRef(null);

  const loadOverview = async () => {
    try {
      setLoadingOverview(true);
      const res = await api.get("/api/admin/system/overview");
      setOverview(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingOverview(false);
    }
  };

  const loadUsage = async () => {
    try {
      setLoadingUsage(true);
      const res = await api.get("/api/admin/usage/users");
      setUsagePayload(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load user usage");
    } finally {
      setLoadingUsage(false);
    }
  };

  const loadAll = async () => {
    await Promise.all([loadOverview(), loadUsage()]);
  };

  const loadLogs = async () => {
    try {
      setLoadingLogs(true);

      const res = await api.get("/api/admin/system/logs", {
        params: {
          service: logService,
          tail: logTail,
        },
      });

      setLogs(
        res.data.ok
          ? res.data.logs || "No logs returned"
          : res.data.error || "Could not read logs"
      );
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (refreshTimerRef.current) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (autoRefresh) {
      refreshTimerRef.current = window.setInterval(() => {
        loadOverview();
      }, 5000);
    }

    return () => {
      if (refreshTimerRef.current) {
        window.clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [autoRefresh]);

  const users = usagePayload?.users || [];

  const stats = useMemo(() => {
    const totalUsers = users.length;
    const totalPatients = users.reduce(
      (sum, user) => sum + (user.patient_count || 0),
      0
    );
    const totalSessions = users.reduce(
      (sum, user) => sum + (user.session_count || 0),
      0
    );
    const lockedUsers = users.filter(
      (user) =>
        user.usage?.patient_limit_reached || user.usage?.session_limit_reached
    ).length;

    return {
      totalUsers,
      totalPatients,
      totalSessions,
      lockedUsers,
    };
  }, [users]);

  const cpu = overview?.cpu?.load_percent_estimate || 0;
  const memory = overview?.memory?.used_percent || 0;
  const disk = overview?.disk_root?.used_percent || 0;

  const handleOpenUser = (user) => {
    setSelectedUser(user);
    setQuotaModalOpen(true);
  };

  const reloadUsageAfterQuotaSave = async () => {
    await loadUsage();
  };

  return (
    <div className="mgadmin-page">
      <style>{adminCss}</style>

      <section className="mgadmin-hero">
        <div className="mgadmin-hero-copy">
          <div className="mgadmin-kicker">Admin Command Center</div>
          <h1>System & Usage Control</h1>
          <p>
            Live server pulse, Docker/local runtime awareness, user quota control,
            and service logs from one MedGen administration workbench.
          </p>

          <div className="mgadmin-actions">
            <button
              type="button"
              className="mgadmin-btn primary"
              onClick={loadAll}
              disabled={loadingOverview || loadingUsage}
            >
              Refresh All
            </button>

            <button
              type="button"
              className={autoRefresh ? "mgadmin-btn active" : "mgadmin-btn soft"}
              onClick={() => setAutoRefresh((prev) => !prev)}
            >
              {autoRefresh ? "Live Refresh On" : "Live Refresh Off"}
            </button>

            <button
              type="button"
              className="mgadmin-btn soft"
              onClick={loadLogs}
              disabled={loadingLogs}
            >
              {loadingLogs ? "Loading Logs..." : "Load Logs"}
            </button>
          </div>

          <LiveWave cpu={cpu} memory={memory} disk={disk} />
        </div>

        <div className="mgadmin-hero-panel">
          <div className="mgadmin-panel-top">
            <div>
              <span>Server Status</span>
              <strong>
                {loadingOverview ? "Checking server" : "Backend online"}
              </strong>
            </div>
            <em>{autoRefresh ? "Live" : "Manual"}</em>
          </div>

          <p>
            CPU, memory, disk and Docker socket visibility show whether the
            machine is healthy or getting cooked under load.
          </p>

          <SystemSignal overview={overview} />
        </div>
      </section>

      <section className="mgadmin-stats-grid">
        <AdminStatCard
          primary
          label="Users"
          value={stats.totalUsers}
          helper="Total users loaded from backend"
        />

        <AdminStatCard
          label="Patients"
          value={stats.totalPatients}
          helper="Total patient profiles across users"
        />

        <AdminStatCard
          label="Sessions"
          value={stats.totalSessions}
          helper="Total clinical sessions created"
        />

        <AdminStatCard
          label="At limit"
          value={stats.lockedUsers}
          helper="Users near or over configured quota"
        />
      </section>

      <section className="mgadmin-system-grid">
        <div className="mgadmin-card">
          <div className="mgadmin-card-head">
            <div>
              <span>System Overview</span>
              <strong>Backend machine</strong>
              <p>Live host behavior for local or Docker runtime.</p>
            </div>

            <button
              className="mgadmin-btn soft"
              type="button"
              onClick={loadOverview}
              disabled={loadingOverview}
            >
              {loadingOverview ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {!overview ? (
            <div className="mgadmin-empty">No system data loaded.</div>
          ) : (
            <div className="mgadmin-system-metrics">
              <div>
                <span>CPU</span>
                <strong>{overview.cpu?.cpu_count} cores</strong>
                <p>{overview.cpu?.model_name}</p>
                <UsageBar value={overview.cpu?.load_percent_estimate || 0} />
              </div>

              <div>
                <span>RAM</span>
                <strong>
                  {overview.memory?.used_gb} / {overview.memory?.total_gb} GB
                </strong>
                <p>{overview.memory?.used_percent}% used</p>
                <UsageBar value={overview.memory?.used_percent || 0} />
              </div>

              <div>
                <span>Disk</span>
                <strong>
                  {overview.disk_root?.used_gb} /{" "}
                  {overview.disk_root?.total_gb} GB
                </strong>
                <p>{overview.disk_root?.used_percent}% used</p>
                <UsageBar value={overview.disk_root?.used_percent || 0} />
              </div>

              <div>
                <span>Uptime</span>
                <strong>
                  {Math.round((overview.host?.uptime_seconds || 0) / 3600)} hrs
                </strong>
                <p>{overview.host?.platform}</p>
              </div>
            </div>
          )}
        </div>

        <DockerLocalStatus overview={overview} />
      </section>

      <section className="mgadmin-card mgadmin-users-card">
        <div className="mgadmin-card-head">
          <div>
            <span>User Usage</span>
            <strong>Quota table</strong>
            <p>
              Click any user row to open quota editor. Free default is 2 patients
              and 2 sessions per patient.
            </p>
          </div>

          <button
            className="mgadmin-btn soft"
            type="button"
            onClick={loadUsage}
            disabled={loadingUsage}
          >
            {loadingUsage ? "Loading..." : "Refresh Users"}
          </button>
        </div>

        {loadingUsage ? (
          <div className="mgadmin-empty">Loading user usage...</div>
        ) : users.length === 0 ? (
          <div className="mgadmin-empty">No users found.</div>
        ) : (
          <UsersTable users={users} onSelectUser={handleOpenUser} />
        )}
      </section>

      <section className="mgadmin-card">
        <div className="mgadmin-card-head">
          <div>
            <span>Service Logs</span>
            <strong>Runtime log viewer</strong>
            <p>
              Works when backend can access Docker socket. Local mode may show
              unavailable logs depending on host permissions.
            </p>
          </div>
        </div>

        <div className="mgadmin-log-controls">
          <select value={logService} onChange={(e) => setLogService(e.target.value)}>
            <option value="backend">Backend</option>
            <option value="gateway">AI Gateway</option>
            <option value="postgres">Postgres</option>
          </select>

          <select value={logTail} onChange={(e) => setLogTail(Number(e.target.value))}>
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>

          <button
            type="button"
            className="mgadmin-btn primary"
            onClick={loadLogs}
            disabled={loadingLogs}
          >
            {loadingLogs ? "Loading..." : "Fetch Logs"}
          </button>
        </div>

        <pre className="mgadmin-log-box">
          {logs || "Select service and fetch logs..."}
        </pre>
      </section>

      <QuotaModal
        user={selectedUser}
        open={quotaModalOpen}
        onClose={() => setQuotaModalOpen(false)}
        onSaved={reloadUsageAfterQuotaSave}
      />
    </div>
  );
}

const adminCss = `
.mgadmin-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;
  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgadmin-page * {
  box-sizing: border-box;
}

.mgadmin-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgadmin-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgadmin-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(38px, 4vw, 58px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgadmin-hero p {
  max-width: 790px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgadmin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;
}

.mgadmin-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
  text-decoration: none;
}

.mgadmin-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgadmin-btn.primary,
.mgadmin-btn.active {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgadmin-btn.soft,
.mgadmin-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgadmin-hero-panel,
.mgadmin-runtime-card {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgadmin-panel-top,
.mgadmin-signal-head,
.mgadmin-card-head,
.mgadmin-runtime-head,
.mgadmin-live-wave-head,
.mgadmin-modal-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgadmin-panel-top span,
.mgadmin-signal-head span,
.mgadmin-card-head span,
.mgadmin-runtime-head span,
.mgadmin-live-wave-head span,
.mgadmin-modal-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgadmin-card-head span,
.mgadmin-modal-head span {
  color: #125a94;
}

.mgadmin-panel-top strong,
.mgadmin-runtime-head strong,
.mgadmin-live-wave-head strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgadmin-card-head strong,
.mgadmin-modal-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 24px;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgadmin-panel-top em,
.mgadmin-signal-head em,
.mgadmin-live-wave-head em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgadmin-hero-panel p,
.mgadmin-runtime-card p {
  color: #a9bdd3;
  margin: 18px 0;
}

.mgadmin-live-wave-card {
  padding: 18px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 12% 0%, rgba(24,183,255,0.16), transparent 34%),
    linear-gradient(145deg, #03060c, #081525);
  border: 1px solid rgba(24,183,255,0.16);
  box-shadow: 0 18px 44px rgba(3,6,12,0.14);
}

.mgadmin-wave-wrap {
  position: relative;
  overflow: hidden;
  height: 190px;
  margin-top: 15px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 50% 50%, rgba(24,183,255,0.08), transparent 42%),
    #03060c;
  border: 1px solid rgba(24,183,255,0.14);
}

.mgadmin-wave-wrap svg {
  width: 100%;
  height: 100%;
}

.mgadmin-wave-grid line {
  stroke: rgba(24,183,255,0.075);
  stroke-width: 1;
}

.mgadmin-wave-line {
  fill: none;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.mgadmin-wave-line.glow {
  stroke: rgba(24,183,255,0.22);
  stroke-width: 9;
  filter: blur(5px);
}

.mgadmin-wave-line.main {
  stroke: url(#mgWaveGradient);
  stroke-width: 3;
  stroke-dasharray: 900;
  stroke-dashoffset: 900;
  animation: mgadminWaveDraw 2.4s linear infinite;
}

.mgadmin-wave-scan {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 120px;
  left: -140px;
  background: linear-gradient(90deg, transparent, rgba(24,183,255,0.14), transparent);
  animation: mgadminWaveScan 2.8s linear infinite;
}

.mgadmin-wave-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
  margin-top: 12px;
}

.mgadmin-wave-metrics div,
.mgadmin-runtime-grid div {
  padding: 12px;
  border-radius: 15px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgadmin-wave-metrics span,
.mgadmin-runtime-grid span {
  display: block;
  margin-bottom: 6px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgadmin-wave-metrics strong,
.mgadmin-runtime-grid strong {
  color: white;
  font-size: 20px;
  font-weight: 950;
}

.mgadmin-signal-card {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgadmin-signal-head strong {
  color: white;
  font-size: 16px;
}

.mgadmin-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: end;
  margin-top: 16px;
}

.mgadmin-bar-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgadmin-bar-track {
  width: 58px;
  height: 88px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgadmin-bar-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  animation: mgadminBarRise 0.75s ease both;
}

.mgadmin-bar-item b {
  color: white;
  font-size: 16px;
  font-weight: 950;
}

.mgadmin-bar-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgadmin-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.mgadmin-stat {
  min-height: 128px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgadmin-stat.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgadmin-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgadmin-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgadmin-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgadmin-stat.primary strong {
  color: white;
}

.mgadmin-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgadmin-stat.primary p {
  color: rgba(255,255,255,0.86);
}

.mgadmin-system-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 18px;
  align-items: stretch;
}

.mgadmin-card {
  padding: 22px;
  border-radius: 22px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  box-shadow: 0 14px 38px rgba(7,17,31,0.06);
}

.mgadmin-card-head {
  margin-bottom: 18px;
}

.mgadmin-card-head p,
.mgadmin-modal-head p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgadmin-system-metrics {
  display: grid;
  gap: 14px;
}

.mgadmin-system-metrics > div {
  padding: 16px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgadmin-system-metrics span,
.mgadmin-current-usage span,
.mgadmin-modal-status-grid span,
.mgadmin-table-metric span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgadmin-system-metrics strong,
.mgadmin-current-usage strong,
.mgadmin-modal-status-grid strong {
  display: block;
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
}

.mgadmin-system-metrics p {
  margin: 7px 0 12px;
  color: var(--mg-muted);
  line-height: 1.45;
}

.mgadmin-runtime-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
}

.mgadmin-status-dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
  margin-top: 4px;
}

.mgadmin-status-dot.ok {
  background: #18b7ff;
  box-shadow: 0 0 0 7px rgba(24,183,255,0.12), 0 0 22px rgba(24,183,255,0.58);
}

.mgadmin-status-dot.bad {
  background: #f59e0b;
  box-shadow: 0 0 0 7px rgba(245,158,11,0.12), 0 0 22px rgba(245,158,11,0.45);
}

.mgadmin-container-list {
  display: grid;
  gap: 10px;
  margin-top: 15px;
}

.mgadmin-container-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  gap: 9px;
  align-items: center;
  padding: 11px;
  border-radius: 14px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgadmin-container-row span {
  color: white;
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mgadmin-container-row em {
  color: #a9bdd3;
  font-size: 11px;
  font-style: normal;
}

.mgadmin-runtime-error {
  margin-top: 14px;
  padding: 12px;
  border-radius: 14px;
  color: #ffd28a;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.18);
  white-space: pre-wrap;
}

.mgadmin-usage-bar {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #dfeaf6;
  border: 1px solid #d1e2f3;
}

.mgadmin-usage-bar i {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #18b7ff, #1f7bc1);
  transition: width 0.35s ease;
}

.mgadmin-table-wrap {
  width: 100%;
  overflow: auto;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
}

.mgadmin-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
  background: white;
}

.mgadmin-table th {
  text-align: left;
  padding: 14px 16px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  background: #eef6ff;
  border-bottom: 1px solid var(--mg-border);
}

.mgadmin-table td {
  padding: 15px 16px;
  border-bottom: 1px solid #e4eef8;
  color: var(--mg-text);
  vertical-align: middle;
}

.mgadmin-table tbody tr {
  cursor: pointer;
  transition: 0.15s ease;
}

.mgadmin-table tbody tr:hover {
  background: #f5fbff;
}

.mgadmin-user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mgadmin-avatar {
  width: 44px;
  height: 44px;
  border-radius: 15px;
  display: grid;
  place-items: center;
  color: #0b4e84;
  font-size: 13px;
  font-weight: 950;
  background: #e6f3ff;
  border: 1px solid #d5e8f7;
  flex: 0 0 auto;
}

.mgadmin-avatar.large {
  width: 58px;
  height: 58px;
  border-radius: 19px;
  font-size: 16px;
}

.mgadmin-user-cell strong {
  display: block;
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
}

.mgadmin-user-cell p {
  margin: 4px 0 0;
  color: var(--mg-muted);
  font-size: 12px;
}

.mgadmin-pill {
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  border: 1px solid rgba(31,123,193,0.18);
  font-size: 11px;
  font-weight: 900;
  text-transform: capitalize;
  white-space: nowrap;
}

.mgadmin-pill.ok {
  color: #065f46;
  background: rgba(16,185,129,0.10);
  border-color: rgba(16,185,129,0.20);
}

.mgadmin-pill.danger {
  color: #991b1b;
  background: rgba(239,68,68,0.10);
  border-color: rgba(239,68,68,0.20);
}

.mgadmin-table-metric {
  min-width: 130px;
  display: grid;
  gap: 7px;
}

.mgadmin-table-metric strong {
  color: var(--mg-text);
  font-size: 13px;
  font-weight: 950;
}

.mgadmin-mini-btn {
  min-height: 32px;
  padding: 7px 12px;
  border-radius: 10px;
  border: 1px solid var(--mg-border);
  color: #112238;
  background: #f8fbff;
  font-weight: 900;
  cursor: pointer;
}

.mgadmin-log-controls {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px;
  margin-bottom: 12px;
}

.mgadmin-log-controls select,
.mgadmin-field input,
.mgadmin-field select,
.mgadmin-field textarea {
  width: 100%;
  min-height: 42px;
  padding: 10px 13px;
  border-radius: 12px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  color: #07111f;
  font-size: 13px;
  font-weight: 750;
  outline: none;
}

.mgadmin-field textarea {
  resize: vertical;
}

.mgadmin-log-box {
  min-height: 360px;
  max-height: 560px;
  overflow: auto;
  padding: 16px;
  border-radius: 18px;
  color: #c8f1ff;
  background: #03060c;
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
}

.mgadmin-empty {
  padding: 24px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  color: var(--mg-muted);
}

.mgadmin-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(3, 6, 12, 0.58);
  backdrop-filter: blur(10px);
}

.mgadmin-modal {
  width: min(920px, 100%);
  max-height: calc(100vh - 44px);
  overflow: auto;
  padding: 24px;
  border-radius: 26px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.11), transparent 34%),
    #ffffff;
  border: 1px solid rgba(205,220,236,0.9);
  box-shadow: 0 35px 90px rgba(3,6,12,0.32);
}

.mgadmin-modal-user {
  display: flex;
  gap: 14px;
  align-items: center;
}

.mgadmin-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--mg-border);
  background: #f8fbff;
  color: var(--mg-text);
  font-size: 25px;
  line-height: 1;
  cursor: pointer;
}

.mgadmin-modal-status-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 20px 0;
}

.mgadmin-modal-status-grid > div,
.mgadmin-current-usage > div {
  padding: 16px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgadmin-editor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 15px;
}

.mgadmin-field {
  display: grid;
  gap: 7px;
  margin-bottom: 15px;
}

.mgadmin-field label {
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgadmin-current-usage {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 15px;
}

.mgadmin-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

@keyframes mgadminBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgadminWaveDraw {
  0% {
    stroke-dashoffset: 900;
  }

  60% {
    stroke-dashoffset: 0;
  }

  100% {
    stroke-dashoffset: -900;
  }
}

@keyframes mgadminWaveScan {
  from {
    left: -140px;
  }

  to {
    left: 100%;
  }
}

@media (max-width: 1280px) {
  .mgadmin-hero,
  .mgadmin-system-grid {
    grid-template-columns: 1fr;
  }

  .mgadmin-hero-panel {
    max-width: 620px;
  }

  .mgadmin-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .mgadmin-page {
    padding: 20px;
  }

  .mgadmin-log-controls,
  .mgadmin-editor-grid,
  .mgadmin-current-usage,
  .mgadmin-modal-status-grid,
  .mgadmin-runtime-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .mgadmin-page {
    padding: 14px;
  }

  .mgadmin-hero,
  .mgadmin-modal {
    padding: 20px;
  }

  .mgadmin-hero h1 {
    font-size: 36px;
  }

  .mgadmin-actions,
  .mgadmin-modal-actions {
    flex-direction: column;
  }

  .mgadmin-btn {
    width: 100%;
  }

  .mgadmin-stats-grid,
  .mgadmin-wave-metrics {
    grid-template-columns: 1fr;
  }

  .mgadmin-modal-head {
    flex-direction: column;
  }

  .mgadmin-icon-btn {
    align-self: flex-end;
  }
}
`;